import apiClient, { STATIC_CONFIG } from './api';

// 学校API服务
export const schoolService = {
  /**
   * 获取学校列表（支持复杂筛选和分页）
   * @param {Object} params - 查询参数
   * @returns {Promise} 学校列表响应
   */
  getSchools: (params = {}) => {
    const queryParams = {
      page: params.page || 0,
      size: params.size || 20,
      sortBy: params.sortBy || 'name',
      sortOrder: params.sortOrder || 'asc',
      ...params
    };

    // 过滤掉空值参数
    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([key, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );

    return apiClient.get('/api/schools');
  },

  /**
   * 获取学校详情
   * @param {string} schoolId - 学校ID
   * @returns {Promise} 学校详情
   */
  getSchoolDetail: (schoolId) => {
    return apiClient.get(`/api/schools/${schoolId}`);
  },

  /**
   * 获取学校的专业列表
   * @param {string} schoolId - 学校ID
   * @param {Object} params - 查询参数
   * @returns {Promise} 专业列表
   */
  getSchoolPrograms: (schoolId, params = {}) => {
    const queryParams = {
      page: params.page || 0,
      size: params.size || 20,
      ...params
    };

    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([key, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );

    return apiClient.get(`/api/schools/${schoolId}/programs`, { params: filteredParams });
  },

  /**
   * 搜索专业
   * @param {Object} params - 搜索参数
   * @returns {Promise} 专业搜索结果
   */
  searchPrograms: (params = {}) => {
    const queryParams = {
      page: params.page || 0,
      size: params.size || 20,
      ...params
    };

    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([key, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );

    return apiClient.get('/api/schools/programs/search', { params: filteredParams });
  },

  /**
   * 获取学位类型
   * @returns {Promise} 学位类型列表
   */
  getDegreeTypes: () => {
    return apiClient.get('/api/schools/degrees');
  },

  /**
   * 获取入学时间选项
   * @returns {Promise} 入学时间选项
   */
  getIntakeOptions: () => {
    return apiClient.get('/api/schools/intakes');
  },

  /**
   * 获取学校统计数据
   * @returns {Promise} 统计数据
   */
  getSchoolStats: () => {
    return apiClient.get('/api/schools/stats');
  }
};

// 数据转换工具函数
export const schoolDataMapper = {
  /**
   * 构建完整的图片URL
   * @param {string} imagePath - 图片路径
   * @param {string} fallback - 默认图片路径
   * @param {string} schoolName - 学校名称（用于生成占位头像）
   * @returns {string} 完整的图片URL
   */
  buildImageUrl: (imagePath, fallback = '/images/universities/default-logo.png', schoolName = '') => {
    // 生成占位头像的辅助函数
    const generateAvatarUrl = (name) => {
      if (!name) return fallback;
      
      const config = STATIC_CONFIG.AVATAR_CONFIG;
      const encodedName = encodeURIComponent(name);
      return `${config.service}?name=${encodedName}&size=${config.size}&background=${config.background}&color=${config.color}&format=${config.format}&rounded=${config.rounded}&bold=${config.bold}`;
    };
    // 如果已经是完整URL，直接返回
    if (imagePath && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) {
      return imagePath;
    }
    
    // 如果有图片路径且启用了baseurl拼接
    if (imagePath && STATIC_CONFIG.ENABLE_IMAGE_BASEURL) {
      // 确保路径以/开头
      const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
      const fullUrl = `${STATIC_CONFIG.IMAGE_BASE_URL}${cleanPath}`;
      
      return fullUrl;
    }
    
    // 如果没有图片路径或未启用baseurl拼接，使用占位头像
    if (schoolName) {
      const avatarUrl = generateAvatarUrl(schoolName);
      return avatarUrl;
    }
    
    return fallback;
  },

  /**
   * 将后端学校数据转换为前端格式
   * @param {Object} apiSchool - 后端学校数据
   * @returns {Object} 前端学校数据格式
   */
  mapSchoolFromApi: (apiSchool) => {
    // 解析成立年份
    const parseEstablishedYear = (EstablishedTime) => {
      if (!EstablishedTime) return -'';
      // 提取年份数字
      const yearMatch = EstablishedTime.toString().match(/(\d{4})/);
      return yearMatch ? parseInt(yearMatch[1]) : '-';
    };

    // 解析排名数据
    const parseRanking = (ranking) => {
      if (!ranking) return '-';
      const numRanking = parseInt(ranking);
      return isNaN(numRanking) ? '-' : numRanking;
    };

    // 提取排名的最小数字
    const extractMinRanking = (rankingStr) => {
      if (!rankingStr) return null;
      const matches = rankingStr.match(/\d+/g);
      if (!matches) return null;
      const nums = matches.map(Number).filter(n => !isNaN(n));
      if (nums.length === 0) return null;
      return Math.min(...nums);
    };

    const schoolName = apiSchool.CnName || apiSchool.EnName || '';
    
    return {
      id: apiSchool.id,
      name: schoolName,
      nameEn: apiSchool.EnName || '',
      Abbreviation: apiSchool.Abbreviation || '',
      ShortName: apiSchool.ShortName || '',
      logo: schoolDataMapper.buildImageUrl(apiSchool.Logo, '/images/universities/default-logo.png', schoolName),
      image: schoolDataMapper.buildImageUrl(apiSchool.Logo, '/images/universities/default-campus.jpg', schoolName),
      ranking: apiSchool.Ranking,
      rankingValue: extractMinRanking(apiSchool.Ranking),
      rankingAsia: apiSchool.RankingAsia,
      localRanking: apiSchool.localRanking,
      location: (() => {
        // 优先使用 Locations 字段（可能是字符串或对象）
        if (apiSchool.Locations) {
          if (typeof apiSchool.Locations === 'string') {
            // 如果是字符串，解析为 state 和 city
            const parts = apiSchool.Locations.split(',').map(s => s.trim());
            return {
              state: parts[0] || '',
              city: parts[1] || '',
              address: apiSchool.address || ''
            };
          } else if (typeof apiSchool.Locations === 'object') {
            // 如果是对象，直接使用
            return {
              state: apiSchool.Locations.state || apiSchool.state || '',
              city: apiSchool.Locations.city || apiSchool.city || '',
              address: apiSchool.Locations.address || apiSchool.address || ''
            };
          }
        }
        // 回退到其他字段
        return {
          state: apiSchool.state || apiSchool.location?.state || '',
          city: apiSchool.city || apiSchool.location?.city || '',
          address: apiSchool.address || apiSchool.location?.address || ''
        };
      })(),
      type: apiSchool.PublicOrPrivate == 1 ? '公立大学' : '私立大学',
      established: parseEstablishedYear(apiSchool.EstablishedTime),
      students: {
        total: apiSchool.NumberOfStudents || '-',
        international: apiSchool.NumberOfInternationalStudents || 0,
        undergraduate: apiSchool.NumberOfUndergraduatesStudents || 0,
        postgraduate: apiSchool.NumberOfPostgraduatesStudents || 0
      },
      faculties: apiSchool.faculties?.map(f => f.name) || ['工程学院', '商学院', '文学院'],
      popularPrograms: [
        { name: '工程学', degree: '学士', duration: '4年' },
        { name: '商科', degree: '学士', duration: '3年' },
        { name: '计算机科学', degree: '学士', duration: '4年' },
        { name: '医学', degree: '学士', duration: '5年' },
        { name: '法学', degree: '学士', duration: '4年' }
      ].slice(0, 3),
      tuition: {
        undergraduate: {
          min: apiSchool.FeesMin || '-',
          max: apiSchool.FeesMax || '-'
        },
        postgraduate: {
          min: (apiSchool.FeesMin || '-') * 1.2,
          max: (apiSchool.FeesMax || '-') * 1.5
        },
        currency: 'RM'
      },
      requirements: {
        undergraduate: {
          academic: 'SPM/STPM/A-Level或同等学历',
          language: 'IELTS 6.0 或 TOEFL 550',
          other: ''
        },
        postgraduate: {
          academic: '学士学位或同等学历',
          language: 'IELTS 6.5 或 TOEFL 580',
          other: ''
        }
      },
      highlights: [
        '马来西亚教育部认证',
        '中国教育部认可',
        '国际化教学环境',
        '优秀师资力量'
      ],
      facilities: [
        '现代化图书馆',
        '实验室设施',
        '学生宿舍',
        '体育设施',
        '餐厅'
      ],
      colors: {
        primary: '#1e3a8a',
        secondary: '#3b82f6'
      },
      websiteUrl: apiSchool.officialWebsite || '',
      campuses: apiSchool.campuses || [],
      medias: apiSchool.medias || [],
      news: apiSchool.news || [],
      // 补充学费区间原始字段，便于前端筛选
      FeesMin: apiSchool.FeesMin,
      FeesMax: apiSchool.FeesMax
    };
  },

  /**
   * 将前端筛选参数转换为后端API参数
   * @param {Object} filters - 前端筛选参数
   * @param {string} searchTerm - 搜索关键词
   * @returns {Object} 后端API参数
   */
  mapFiltersToApiParams: (filters, searchTerm = '') => {
    const params = {};

    // 搜索关键词
    if (searchTerm) {
      params.keyword = searchTerm;
    }

    // 地区筛选
    if (filters.location) {
      params.state = filters.location;
    }

    // 类型筛选
    if (filters.type) {
      params.isPublic = filters.type === '公立大学';
    }

    // 排名筛选
    if (filters.ranking) {
      switch (filters.ranking) {
        case 'top50':
          params.maxRanking = 50;
          break;
        case 'top100':
          params.maxRanking = 100;
          break;
        case 'top200':
          params.maxRanking = 200;
          break;
        case 'top500':
          params.maxRanking = 500;
          break;
        default:
          // 不设置排名限制
          break;
      }
    }

    // 学费范围
    if (filters.tuitionRange && filters.tuitionRange.length === 2) {
      params.minFee = filters.tuitionRange[0].toString();
      params.maxFee = filters.tuitionRange[1].toString();
    }

    // 专业筛选
    if (filters.programs && filters.programs.length > 0) {
      params.degrees = filters.programs.join(',');
    }

    // 排序
    if (filters.sortBy) {
      switch (filters.sortBy) {
        case 'ranking':
          params.sortBy = 'ranking';
          params.sortOrder = 'asc';
          break;
        case 'tuition-low':
          params.sortBy = 'fee';
          params.sortOrder = 'asc';
          break;
        case 'tuition-high':
          params.sortBy = 'fee';
          params.sortOrder = 'desc';
          break;
        case 'students':
          params.sortBy = 'students';
          params.sortOrder = 'desc';
          break;
        case 'established':
          params.sortBy = 'establishedYear';
          params.sortOrder = 'desc';
          break;
        default:
          params.sortBy = 'name';
          params.sortOrder = 'asc';
          break;
      }
    }

    return params;
  }
}; 